import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Check, Sparkles, Zap, Crown } from 'lucide-react';

/**
 * Single source of truth for the plan catalog.
 * If you change pricing, change it here only — PurchasePage, SignupPage and
 * the Settings page all consume from this list.
 *
 * Yearly savings:
 *   $12 × 12 = $144  ➜  $120 yearly = $24 saved = 17 % off
 */
const MONTHLY = 12;
const YEARLY = 120;
const YEARLY_DISCOUNT_PCT = Math.round((1 - YEARLY / (MONTHLY * 12)) * 100);

/**
 * Build a ready-to-send mailto URL.
 * The body is fully written so the user can hit Send without typing
 * anything — the team gets a clean enquiry with the plan name + price.
 */
const buildMailto = (planName, price, period) => {
  const subject = `AV Simulation Studio – ${planName} (${price}/${period.replace('per ', '')}) enquiry`;
  const body = [
    `Hi Team,`,
    ``,
    `I'd like to activate the ${planName} plan (${price} ${period}) for AV Simulation Studio.`,
    ``,
    `Please share the next steps and a payment link.`,
    ``,
    `Thanks,`,
    `[Your name]`,
    `[Company / Studio]`,
    `[Phone / WhatsApp]`,
  ].join('\n');
  return `admin@ideationav.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

export const PLANS = [
  {
    id: 'demo',
    name: '15-Day Free Demo',
    icon: Sparkles,
    price: 'Free',
    period: '15 days',
    accent: 'cyan',
    features: [
      'Full projection / LED / truss tools',
      'Up to 7 projectors per scene',
      'Cloud project save & load',
      'Browser-based — no install',
    ],
    cta: 'Start Free Demo',
    ctaLink: '/signup',
  },
  {
    id: 'pro-monthly',
    name: 'Pro Monthly',
    icon: Zap,
    price: `$${MONTHLY}`,
    period: 'per month',
    accent: 'fuchsia',
    badge: 'Most Popular',
    features: [
      'Unlimited projectors per scene',
      'Periscope, UST, 90° lens libraries',
      'Stacking + overlap blends',
      'Priority email support',
      'Project sharing & viewer links',
    ],
    cta: 'Choose Monthly',
    mailto: buildMailto('Pro Monthly', `$${MONTHLY}`, 'per month'),
  },
  {
    id: 'pro-yearly',
    name: 'Pro Yearly',
    icon: Crown,
    price: `$${YEARLY}`,
    period: 'per year',
    accent: 'amber',
    badge: `Save ${YEARLY_DISCOUNT_PCT}%`,
    monthlyEquivalent: `≈ $${(YEARLY / 12).toFixed(2)}/mo`,
    features: [
      'Everything in Pro Monthly',
      `${YEARLY_DISCOUNT_PCT}% discount vs monthly billing`,
      `Save $${(MONTHLY * 12) - YEARLY}/year`,
      'Priority onboarding',
      'Lock the price for 12 months',
    ],
    cta: 'Choose Yearly',
    mailto: buildMailto('Pro Yearly', `$${YEARLY}`, 'per year'),
  },
];

const ACCENT = {
  cyan:    { ring: 'ring-cyan-400/30',    chip: 'text-cyan-200 bg-cyan-500/10 border-cyan-400/30',   btn: 'border-cyan-400/40 hover:border-cyan-300 text-cyan-200',  icon: 'text-cyan-300' },
  fuchsia: { ring: 'ring-fuchsia-400/40', chip: 'text-fuchsia-100 bg-fuchsia-500/15 border-fuchsia-400/40', btn: 'auth-submit text-white',                                         icon: 'text-fuchsia-300' },
  amber:   { ring: 'ring-amber-400/40',   chip: 'text-amber-200 bg-amber-500/10 border-amber-400/40',  btn: 'border-amber-400/40 hover:border-amber-300 text-amber-200', icon: 'text-amber-300' },
};

/**
 * Generic plan-card grid used in Purchase, Signup and Settings.
 *
 * Props:
 *   compact      — smaller padding / fewer features for in-form embeds
 *   showAllCtas  — when false, all CTAs are silent <span>s (preview only)
 *   className    — pass through
 */
export default function PricingPlans({ compact = false, showAllCtas = true, className = '', onSelectPaid = null }) {
  return (
    <div className={`grid sm:grid-cols-3 gap-3 ${className}`} data-testid="pricing-plans">
      {PLANS.map((plan) => {
        const A = ACCENT[plan.accent];
        const Icon = plan.icon;
        const features = compact ? plan.features.slice(0, 3) : plan.features;
        return (
          <div
            key={plan.id}
            className={`relative auth-glass rounded-2xl ${compact ? 'p-4' : 'p-5'} flex flex-col ring-1 ${A.ring}`}
            data-testid={`pricing-plan-${plan.id}`}
          >
            {plan.badge && (
              <span className={`absolute -top-2 right-3 text-[9px] tracking-[0.18em] uppercase font-semibold px-2 py-0.5 rounded-full border ${A.chip}`}>
                {plan.badge}
              </span>
            )}
            <Icon className={`w-5 h-5 mb-2 ${A.icon}`} />
            <h3 className={`text-white font-semibold ${compact ? 'text-xs' : 'text-sm'}`}>{plan.name}</h3>
            <div className="mt-2 flex items-baseline gap-1">
              <span className={`text-white font-bold ${compact ? 'text-xl' : 'text-2xl'}`}>{plan.price}</span>
              <span className="text-zinc-500 text-[10px]">/ {plan.period}</span>
            </div>
            {plan.monthlyEquivalent && (
              <p className="text-[10px] text-amber-300/80 mt-0.5" data-testid={`pricing-monthly-eq-${plan.id}`}>
                {plan.monthlyEquivalent}
              </p>
            )}
            <ul className={`mt-3 space-y-1 ${compact ? 'text-[10px]' : 'text-[11px]'} text-zinc-300 leading-relaxed flex-1`}>
              {features.map((f) => (
                <li key={f} className="flex gap-2">
                  <Check className="w-3 h-3 mt-0.5 shrink-0 text-emerald-400" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            {showAllCtas && (
              plan.ctaLink ? (
                <Link to={plan.ctaLink} className="mt-3 block">
                  <Button className={`w-full h-9 rounded-lg text-[11px] font-semibold ${A.btn} ${plan.accent !== 'fuchsia' ? 'bg-zinc-900/60 border' : ''}`}
                          data-testid={`pricing-cta-${plan.id}`}>
                    {plan.cta}
                  </Button>
                </Link>
              ) : onSelectPaid ? (
                <button type="button" onClick={() => onSelectPaid(plan.id)} className="mt-3 block w-full">
                  <Button className={`w-full h-9 rounded-lg text-[11px] font-semibold ${A.btn} ${plan.accent !== 'fuchsia' ? 'bg-zinc-900/60 border' : ''}`}
                          data-testid={`pricing-cta-${plan.id}`}>
                    {plan.cta}
                  </Button>
                </button>
              ) : (
                <a href={`mailto:${plan.mailto}`} className="mt-3 block">
                  <Button className={`w-full h-9 rounded-lg text-[11px] font-semibold ${A.btn} ${plan.accent !== 'fuchsia' ? 'bg-zinc-900/60 border' : ''}`}
                          data-testid={`pricing-cta-${plan.id}`}>
                    {plan.cta}
                  </Button>
                </a>
              )
            )}
          </div>
        );
      })}
    </div>
  );
}
